import Phaser from "phaser";
import { loadUserState, saveUserState } from "../state/UserState";
import { SocialRewardAdapter } from "../adapters/SocialRewardAdapter";
import { GAME_WIDTH, GAME_HEIGHT } from "../app/Config";

interface ResultData {
  score: number;
  hits: number;
  stageIndex: number;
}

export class ResultScene extends Phaser.Scene {
  private socialAdapter = new SocialRewardAdapter();

  constructor() {
    super({ key: "ResultScene" });
  }

  create(data: ResultData): void {
    const { score, hits, stageIndex } = data;
    const cx = GAME_WIDTH / 2;

    // Update best score
    const userState = loadUserState();
    const isNewBest = score > userState.bestScore;
    if (isNewBest) {
      userState.bestScore = score;
      saveUserState(userState);
    }

    // Background
    this.add
      .rectangle(cx, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x0e0e2c)
      .setDepth(0);

    // Title
    this.add
      .text(cx, 60, "RESULT", {
        fontFamily: "monospace",
        fontSize: "36px",
        color: "#ffffff",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(10);

    // Score
    this.add
      .text(cx, 130, `Score: ${score}`, {
        fontFamily: "monospace",
        fontSize: "28px",
        color: "#00ff88",
      })
      .setOrigin(0.5)
      .setDepth(10);

    if (isNewBest) {
      this.add
        .text(cx, 165, "NEW BEST!", {
          fontFamily: "monospace",
          fontSize: "18px",
          color: "#ffdd00",
        })
        .setOrigin(0.5)
        .setDepth(10);
    }

    // Best Score
    this.add
      .text(cx, 200, `Best: ${userState.bestScore}`, {
        fontFamily: "monospace",
        fontSize: "20px",
        color: "#aaaaaa",
      })
      .setOrigin(0.5)
      .setDepth(10);

    // Hits / Stage
    this.add
      .text(cx, 240, `Hits: ${hits}  |  Stage: ${stageIndex + 1}`, {
        fontFamily: "monospace",
        fontSize: "18px",
        color: "#cccccc",
      })
      .setOrigin(0.5)
      .setDepth(10);

    // ---- Boost Ticket Section ----
    const ticketsLeft = userState.boostTicketsRemaining;
    const canClaim = userState.boostClaimCount < 3;

    if (ticketsLeft > 0) {
      this.createButton(cx, 310, `Boost Play (${ticketsLeft} left)`, () => {
        userState.boostTicketsRemaining -= 1;
        saveUserState(userState);
        this.scene.start("PlayScene", { isBoostActive: true });
      });
    }

    if (canClaim) {
      this.createButton(
        cx,
        ticketsLeft > 0 ? 370 : 310,
        `Follow & Repost (+1 Boost, max 3)`,
        () => {
          this.socialAdapter.openFollowUrl();
          // Self-reported: grant ticket on return
          userState.boostTicketsRemaining += 1;
          userState.boostClaimCount += 1;
          saveUserState(userState);
          // Refresh scene
          this.scene.restart(data);
        }
      );
    }

    // ---- X Share ----
    this.createButton(cx, 430, "Share on X", () => {
      this.socialAdapter.openRepostUrl(
        `Eye Catcher - Score: ${score} (Stage ${stageIndex + 1})`,
        window.location.href
      );
    });

    // ---- Retry ----
    this.createButton(cx, 500, "RETRY", () => {
      this.scene.start("PlayScene");
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    onClick: () => void
  ): void {
    const btnBg = this.add
      .rectangle(x, y, 340, 44, 0x333366, 1)
      .setInteractive({ useHandCursor: true })
      .setDepth(10);
    const btnText = this.add
      .text(x, y, label, {
        fontFamily: "monospace",
        fontSize: "16px",
        color: "#ffffff",
      })
      .setOrigin(0.5)
      .setDepth(11);

    btnBg.on("pointerover", () => btnBg.setFillStyle(0x4444aa));
    btnBg.on("pointerout", () => btnBg.setFillStyle(0x333366));
    btnBg.on("pointerdown", onClick);
  }
}
